import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Check, Plus, Minus, Timer, Loader2, Info, X } from 'lucide-react';
import { useWorkoutPlan, useExercisesByIds, saveWorkoutSession } from '@/hooks/useWorkout';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { RATING_META, type SetData } from '@/types/workout';

export default function WorkoutSessionScreen() {
  const { planId, sessionIdx } = useParams<{ planId: string; sessionIdx: string }>();
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();

  const sessionIndex = Number(sessionIdx ?? 0);
  const { plan, loading: planLoading } = useWorkoutPlan(planId ?? null);

  const session = plan?.plan_data?.sessions[sessionIndex];
  const exerciseIds = session?.exercises.map((e) => e.exercise_id) ?? [];
  const { exercises: exerciseMap, loading: exLoading } = useExercisesByIds(exerciseIds);

  const [currentExIdx, setCurrentExIdx] = useState(0);
  const [setsData, setSetsData] = useState<Record<string, SetData[]>>({});
  const [startedAt] = useState(new Date());
  const [restTimer, setRestTimer] = useState<number | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [showRpeInfo, setShowRpeInfo] = useState(false);
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5 | null>(null);
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Черновик ручного ввода веса (ключ: `${exercise_id}-${setIndex}`), чтобы корректно печатать дробные (12.5)
  const [weightDraft, setWeightDraft] = useState<Record<string, string>>({});

  const restIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!session) return;
    const initial: Record<string, SetData[]> = {};
    session.exercises.forEach((ex) => {
      const defaultSets = Array.from({ length: ex.sets }, (_, i) => ({
        set: i + 1,
        reps: parseInt(ex.reps) || 10,
        weight_kg: null,
        done: false,
      } as SetData & { done: boolean }));
      initial[ex.exercise_id] = defaultSets;
    });
    setSetsData(initial);
  }, [session]);

  useEffect(() => {
    if (restTimer === null) {
      if (restIntervalRef.current) clearInterval(restIntervalRef.current);
      return;
    }
    restIntervalRef.current = setInterval(() => {
      setRestTimer((prev) => {
        if (prev === null || prev <= 1) {
          haptic('success');
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (restIntervalRef.current) clearInterval(restIntervalRef.current); };
  }, [restTimer]);

  const currentPlanEx = session?.exercises[currentExIdx];
  const currentExercise = currentPlanEx ? exerciseMap[currentPlanEx.exercise_id] : null;
  const currentSets = currentPlanEx ? (setsData[currentPlanEx.exercise_id] ?? []) : [];

  function updateSet(setIndex: number, field: 'reps' | 'weight_kg' | 'rpe' | 'rir', value: number | null) {
    if (!currentPlanEx) return;
    setSetsData((prev) => {
      const sets = [...(prev[currentPlanEx.exercise_id] ?? [])];
      sets[setIndex] = { ...sets[setIndex], [field]: value };
      return { ...prev, [currentPlanEx.exercise_id]: sets };
    });
  }

  function markSetDone(setIndex: number) {
    if (!currentPlanEx) return;
    haptic('medium');
    setSetsData((prev) => {
      const sets = [...(prev[currentPlanEx.exercise_id] ?? [])];
      (sets[setIndex] as any).done = true;
      return { ...prev, [currentPlanEx.exercise_id]: sets };
    });
    const restSec = currentPlanEx.rest_seconds ?? 60;
    setRestTimer(restSec);
  }

  function goNext() {
    haptic('light');
    setRestTimer(null);
    if (currentExIdx < (session?.exercises.length ?? 0) - 1) {
      setCurrentExIdx((i) => i + 1);
    } else {
      setFinishing(true);
    }
  }

  function goPrev() {
    haptic('light');
    setRestTimer(null);
    if (currentExIdx > 0) setCurrentExIdx((i) => i - 1);
  }

  async function finishWorkout() {
    if (!profile?.id || !plan || !session) return;
    setSaving(true);

    const exerciseLogs = session.exercises.map((planEx, idx) => {
      const sets = (setsData[planEx.exercise_id] ?? []).filter((s: any) => s.done);
      return {
        exercise_id: planEx.exercise_id,
        planned_sets: planEx.sets,
        planned_reps: planEx.reps,
        sets_data: sets.map((s) => ({
          set: s.set,
          reps: s.reps,
          weight_kg: s.weight_kg,
        })),
        position: idx,
      };
    });

    try {
      await saveWorkoutSession({
        user_id: profile.id,
        plan_id: plan.id,
        session_index: sessionIndex,
        session_name: session.name,
        started_at: startedAt,
        finished_at: new Date(),
        exercise_logs: exerciseLogs,
        rating,
        notes: sessionNotes || null,
        plan_week: plan.current_week,
      });

      haptic('success');
      nav(`/workout/plan/${plan.id}`, { replace: true });
    } catch (e: any) {
      alert(e.message ?? 'Ошибка сохранения');
      setSaving(false);
    }
  }

  if (planLoading || exLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-glow" />
      </div>
    );
  }

  if (!session || !plan) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-4xl mb-3">🤔</div>
          <div className="font-semibold">Тренировка не найдена</div>
          <button onClick={() => nav(-1)} className="mt-4 text-emerald-glow text-sm">← Назад</button>
        </div>
      </div>
    );
  }

  if (finishing) {
    const elapsed = Math.round((Date.now() - startedAt.getTime()) / 1000);
    const totalTonnage = Object.values(setsData).flat().reduce(
      (s, set) => s + set.reps * (set.weight_kg ?? 0), 0
    );

    return (
      <div className="min-h-screen flex flex-col px-4 py-6">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🎉</div>
          <h2 className="font-display font-bold text-xl">Тренировка завершена!</h2>
          <div className="flex justify-center gap-6 mt-4 text-center">
            <div>
              <div className="font-bold text-lg">{formatDuration(elapsed)}</div>
              <div className="text-[10px] text-ink-dim">Время</div>
            </div>
            <div>
              <div className="font-bold text-lg">{Math.round(totalTonnage)} кг</div>
              <div className="text-[10px] text-ink-dim">Тоннаж</div>
            </div>
            <div>
              <div className="font-bold text-lg">{Object.values(setsData).flat().filter((s: any) => s.done).length}</div>
              <div className="text-[10px] text-ink-dim">Подходов</div>
            </div>
          </div>
        </div>

        <div className="bg-bg-card border border-line rounded-2xl p-4 space-y-3">
          <div className="text-sm font-semibold text-center">Как прошла тренировка?</div>
          <div className="flex justify-between px-2">
            {RATING_META.map(({ value, emoji, label }) => (
              <button
                key={value}
                onClick={() => { haptic('light'); setRating(value); }}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                  rating === value ? 'bg-emerald-glow/15 scale-110' : ''
                }`}
              >
                <span className="text-2xl">{emoji}</span>
                <span className="text-[9px] text-ink-dim">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <textarea
          value={sessionNotes}
          onChange={(e) => setSessionNotes(e.target.value)}
          placeholder="Заметки (опционально)..."
          rows={3}
          className="mt-3 w-full bg-bg-card border border-line rounded-xl p-3 text-sm placeholder:text-ink-dim outline-none resize-none focus:border-emerald-glow/50 transition-colors"
        />

        <button
          onClick={finishWorkout}
          disabled={saving}
          className="mt-6 w-full bg-emerald-glow text-bg font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          {saving ? 'Сохраняю...' : 'Сохранить'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Хедер */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button onClick={() => nav(-1)} className="p-2 -ml-2">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{session.name}</div>
          <div className="text-[10px] text-ink-dim">
            Упражнение {currentExIdx + 1} из {session.exercises.length}
          </div>
        </div>
        <button
          onClick={() => setFinishing(true)}
          className="text-[11px] text-ink-dim px-2 py-1 rounded-lg border border-line"
        >
          Завершить
        </button>
      </div>

      {/* Прогресс-бар */}
      <div className="h-0.5 bg-line mx-4 rounded-full mb-1">
        <div
          className="h-full bg-emerald-glow rounded-full transition-all"
          style={{ width: `${((currentExIdx + 1) / session.exercises.length) * 100}%` }}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentExIdx}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
            className="p-4 space-y-4"
          >
            {/* GIF упражнения */}
            {currentExercise?.gif_url && (
              <GifPlayer url={currentExercise.gif_url} name={currentExercise.name} />
            )}

            {/* Название */}
            <div>
              <h2 className="font-display font-bold text-lg">{currentExercise?.name ?? currentPlanEx?.exercise_id}</h2>
              <div className="text-[11px] text-ink-muted mt-0.5">
                {currentPlanEx?.sets} подхода × {currentPlanEx?.reps} повт · отдых {currentPlanEx?.rest_seconds ?? 60}с
              </div>
            </div>

            {/* Техника выполнения */}
            {(currentExercise?.instructions_ru || currentExercise?.instructions_en) && (
              <div className="bg-bg-elevated border border-line rounded-xl p-3 space-y-2">
                <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold">Техника</div>
                <ol className="space-y-1.5">
                  {(currentExercise.instructions_ru || currentExercise.instructions_en)!
                    .split(/\.\s+/)
                    .filter(Boolean)
                    .map((step, i) => (
                      <li key={i} className="flex gap-2 text-[11px] text-ink-muted leading-relaxed">
                        <span className="shrink-0 w-4 h-4 rounded-full bg-emerald-glow/20 text-emerald-glow text-[9px] flex items-center justify-center mt-0.5 font-bold">
                          {i + 1}
                        </span>
                        <span>{step.trim().replace(/\.$/, '')}</span>
                      </li>
                    ))}
                </ol>
              </div>
            )}

            {/* Таймер отдыха */}
            <AnimatePresence>
              {restTimer !== null && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex items-center gap-3 bg-sky-glow/10 border border-sky-glow/30 rounded-xl p-3"
                >
                  <Timer size={18} className="text-sky-glow shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Отдых: {restTimer}с</div>
                    <div className="mt-1 h-1 bg-line rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-sky-glow"
                        style={{ width: `${(restTimer / (currentPlanEx?.rest_seconds ?? 60)) * 100}%` }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setRestTimer(null)}
                    className="text-[10px] text-ink-dim px-2 py-1 border border-line rounded-lg"
                  >
                    Пропустить
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Подходы */}
            <div className="space-y-2">
              {/* Заголовок с кнопкой-справкой */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold">Подходы</span>
                <button
                  onClick={() => setShowRpeInfo(true)}
                  className="text-ink-dim active:text-emerald-glow transition-colors"
                >
                  <Info size={13} />
                </button>
              </div>

              {currentSets.map((set, si) => {
                const done = (set as any).done;
                return (
                  <div key={si} className="space-y-1.5">
                    <div
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        done ? 'border-emerald-glow/40 bg-emerald-glow/5' : 'border-line bg-bg-card'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold ${
                        done ? 'bg-emerald-glow text-bg' : 'bg-bg-elevated text-ink-dim'
                      }`}>
                        {done ? <Check size={13} /> : si + 1}
                      </div>

                      {/* Повторения */}
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => updateSet(si, 'reps', Math.max(1, set.reps - 1))}
                          className="w-7 h-7 rounded-lg bg-bg-elevated flex items-center justify-center active:scale-90">
                          <Minus size={12} />
                        </button>
                        <div className="text-center w-10">
                          <div className="font-bold text-base">{set.reps}</div>
                          <div className="text-[9px] text-ink-dim">повт</div>
                        </div>
                        <button onClick={() => updateSet(si, 'reps', set.reps + 1)}
                          className="w-7 h-7 rounded-lg bg-bg-elevated flex items-center justify-center active:scale-90">
                          <Plus size={12} />
                        </button>
                      </div>

                      {/* Вес */}
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => {
                            const key = `${currentPlanEx?.exercise_id}-${si}`;
                            setWeightDraft((d) => { const n = { ...d }; delete n[key]; return n; });
                            updateSet(si, 'weight_kg', Math.max(0, (set.weight_kg ?? 0) - 2.5));
                          }}
                          className="w-7 h-7 rounded-lg bg-bg-elevated flex items-center justify-center active:scale-90">
                          <Minus size={12} />
                        </button>
                        <div className="text-center w-14">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={weightDraft[`${currentPlanEx?.exercise_id}-${si}`] ?? (set.weight_kg ?? '')}
                            onChange={(e) => {
                              let raw = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                              const parts = raw.split('.');
                              if (parts.length > 2) raw = parts[0] + '.' + parts.slice(1).join('');
                              const key = `${currentPlanEx?.exercise_id}-${si}`;
                              setWeightDraft((d) => ({ ...d, [key]: raw }));
                              updateSet(si, 'weight_kg', raw === '' || raw === '.' ? null : parseFloat(raw));
                            }}
                            placeholder="—"
                            className="w-full font-bold text-base bg-transparent text-center outline-none focus:text-emerald-glow"
                          />
                          <div className="text-[9px] text-ink-dim">кг (опц)</div>
                        </div>
                        <button onClick={() => {
                            const key = `${currentPlanEx?.exercise_id}-${si}`;
                            setWeightDraft((d) => { const n = { ...d }; delete n[key]; return n; });
                            updateSet(si, 'weight_kg', (set.weight_kg ?? 0) + 2.5);
                          }}
                          className="w-7 h-7 rounded-lg bg-bg-elevated flex items-center justify-center active:scale-90">
                          <Plus size={12} />
                        </button>
                      </div>

                      {!done && (
                        <button onClick={() => markSetDone(si)}
                          className="ml-auto px-3 py-1.5 rounded-xl bg-emerald-glow/15 text-emerald-glow text-[11px] font-semibold active:scale-95">
                          ✓
                        </button>
                      )}
                    </div>

                    {/* RPE / RIR — появляются после выполнения подхода */}
                    <AnimatePresence>
                      {done && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex gap-3 px-1">
                            {/* RPE */}
                            <div className="flex-1">
                              <div className="text-[9px] text-ink-dim mb-1">RPE (нагрузка)</div>
                              <div className="flex gap-1">
                                {[6,7,8,9,10].map((v) => (
                                  <button
                                    key={v}
                                    onClick={() => { haptic('light'); updateSet(si, 'rpe', set.rpe === v ? null : v); }}
                                    className={`flex-1 py-1 rounded-lg text-[11px] font-bold transition-all ${
                                      set.rpe === v
                                        ? v >= 9 ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                                          : v === 8 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                                          : 'bg-emerald-glow/20 text-emerald-glow border border-emerald-glow/40'
                                        : 'bg-bg-elevated text-ink-dim'
                                    }`}
                                  >
                                    {v}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* RIR */}
                            <div className="flex-1">
                              <div className="text-[9px] text-ink-dim mb-1">RIR (запас)</div>
                              <div className="flex gap-1">
                                {[0,1,2,3,4].map((v) => (
                                  <button
                                    key={v}
                                    onClick={() => { haptic('light'); updateSet(si, 'rir', set.rir === v ? null : v); }}
                                    className={`flex-1 py-1 rounded-lg text-[11px] font-bold transition-all ${
                                      set.rir === v
                                        ? v === 0 ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                                          : v <= 2 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                                          : 'bg-bg-elevated text-emerald-glow border border-emerald-glow/30'
                                        : 'bg-bg-elevated text-ink-dim'
                                    }`}
                                  >
                                    {v === 4 ? '4+' : v}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* Справка RPE/RIR */}
            <AnimatePresence>
              {showRpeInfo && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-end"
                  onClick={() => setShowRpeInfo(false)}
                >
                  <div className="absolute inset-0 bg-black/50" />
                  <motion.div
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full bg-bg border-t border-line rounded-t-2xl p-5 space-y-4"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-bold text-base">RPE и RIR — что это?</div>
                      <button onClick={() => setShowRpeInfo(false)}>
                        <X size={18} className="text-ink-dim" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-bg-card border border-line rounded-xl p-3">
                        <div className="font-semibold text-sm mb-1">RPE — Rating of Perceived Exertion</div>
                        <div className="text-[12px] text-ink-muted leading-relaxed">
                          Оценка воспринимаемой нагрузки по шкале <strong>6–10</strong>. Показывает насколько тяжёл был подход.
                        </div>
                        <div className="mt-2 space-y-1 text-[11px]">
                          <div className="flex gap-2"><span className="text-emerald-glow font-bold">RPE 6</span><span className="text-ink-dim">— очень легко, можно сделать ещё 4+ повтора</span></div>
                          <div className="flex gap-2"><span className="text-emerald-glow font-bold">RPE 7</span><span className="text-ink-dim">— умеренно, ещё 3 повтора в запасе</span></div>
                          <div className="flex gap-2"><span className="text-amber-400 font-bold">RPE 8</span><span className="text-ink-dim">— тяжело, ещё 2 повтора в запасе</span></div>
                          <div className="flex gap-2"><span className="text-rose-400 font-bold">RPE 9</span><span className="text-ink-dim">— очень тяжело, 1 повтор в запасе</span></div>
                          <div className="flex gap-2"><span className="text-rose-400 font-bold">RPE 10</span><span className="text-ink-dim">— полный отказ, больше не можешь</span></div>
                        </div>
                      </div>

                      <div className="bg-bg-card border border-line rounded-xl p-3">
                        <div className="font-semibold text-sm mb-1">RIR — Reps in Reserve</div>
                        <div className="text-[12px] text-ink-muted leading-relaxed">
                          Повторения в запасе — сколько ещё повторов ты мог бы сделать до полного отказа.
                        </div>
                        <div className="mt-2 space-y-1 text-[11px]">
                          <div className="flex gap-2"><span className="text-rose-400 font-bold">RIR 0</span><span className="text-ink-dim">— отказ, больше не можешь</span></div>
                          <div className="flex gap-2"><span className="text-amber-400 font-bold">RIR 1</span><span className="text-ink-dim">— мог бы сделать ещё 1 повтор</span></div>
                          <div className="flex gap-2"><span className="text-amber-400 font-bold">RIR 2</span><span className="text-ink-dim">— ещё 2 повтора</span></div>
                          <div className="flex gap-2"><span className="text-emerald-glow font-bold">RIR 3-4+</span><span className="text-ink-dim">— лёгкая нагрузка, много запаса</span></div>
                        </div>
                      </div>

                      <div className="text-[11px] text-ink-dim text-center">
                        Рабочий диапазон для роста мышц: <strong>RPE 7–9 / RIR 1–3</strong>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Навигация */}
      <div className="flex gap-3 px-4 py-4 border-t border-line">
        <button
          onClick={goPrev}
          disabled={currentExIdx === 0}
          className="flex-1 py-3 rounded-2xl border border-line text-sm font-medium disabled:opacity-30 active:scale-[0.98] transition-all"
        >
          ← Назад
        </button>
        <button
          onClick={goNext}
          className="flex-1 py-3 rounded-2xl bg-emerald-glow text-bg text-sm font-semibold active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
        >
          {currentExIdx === session.exercises.length - 1 ? 'Завершить 🎉' : 'Следующее →'}
        </button>
      </div>
    </div>
  );
}

function GifPlayer({ url, name }: { url: string; name: string }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="rounded-2xl overflow-hidden bg-bg-elevated aspect-video flex items-center justify-center">
      {!loaded && !error && (
        <Loader2 className="w-6 h-6 animate-spin text-emerald-glow" />
      )}
      {error ? (
        <div className="text-center text-ink-dim">
          <div className="text-3xl mb-1">💪</div>
          <div className="text-[10px]">{name}</div>
        </div>
      ) : (
        <img
          src={url}
          alt={name}
          onLoad={() => setLoaded(true)}
          onError={() => { setError(true); setLoaded(true); }}
          className={`w-full h-full object-contain transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
